import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, Pencil, Archive, ArchiveRestore, ArrowLeft, Loader2, Upload, Trash2, Search, HardHat, Users2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useForm } from "../../components/forms/useForm";
import { Field, TextInput, NumberInput, DateInput, SelectInput, Textarea } from "../../components/forms/Field";
import { required, email as emailV, phone as phoneV, passwordMin, compose } from "../../lib/validation";
import { Toast, ConfirmDialog, PhotoThumb } from "./assets";
import EmptyState from "../../components/EmptyState";
import dict from "../../i18n/es-MX";
import { fmtDate } from "../../lib/dateFormat";

const fmtMXN = (n) => n == null ? "—" : new Intl.NumberFormat("es-MX",{style:"currency",currency:"MXN",maximumFractionDigits:0}).format(n);
const daysUntil = (iso) => iso ? Math.floor((new Date(iso)-new Date())/86400000) : null;

function LicenseBadge({ expiry }) {
    const d = daysUntil(expiry); if (d == null) return <span className="text-slate-500">—</span>;
    if (d < 0) return <span className="text-red-400 font-semibold">{dict.operators.licenseExpired}</span>;
    if (d <= 30) return <span className="text-brand-orange">{d} días</span>;
    return <span className="text-slate-300">{new Date(expiry).toLocaleDateString("es-MX")}</span>;
}

// ============================================================
// OPERATORS
// ============================================================

export function OperatorsList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [archived, setArchived] = useState(false);
    const [toast, setToast] = useState(null);
    const [confirm, setConfirm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get("/operators",{params:archived?{archived:true}:{}}); setItems(r.data); }
        catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
        finally { setLoading(false); }
    }, [archived]);
    useEffect(() => { load(); }, [load]);

    const doArchive = async (id, isArchived) => {
        try { await api.post(`/operators/${id}/${isArchived?"restore":"archive"}`); load(); setToast({msg:dict.common.saved,type:"success"}); }
        catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
        setConfirm(null);
    };

    return <div data-testid="operators-page" className="space-y-5">
        <header className="flex items-start justify-between flex-wrap gap-3">
            <div><h1 className="font-display text-4xl sm:text-5xl font-extrabold">{dict.operators.title}</h1><p className="mt-2 text-slate-400 text-sm">{dict.operators.subtitle}</p></div>
            <button onClick={()=>navigate("/operadores/nuevo")} data-testid="new-operator-button" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-brand-orange text-slate-950 font-semibold uppercase tracking-wider text-sm"><Plus size={18}/>{dict.operators.newOperator}</button>
        </header>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={archived} onChange={e=>setArchived(e.target.checked)} data-testid="filter-archived"/>{dict.common.showArchived}</label>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="operators-table">
                <thead className="bg-slate-900/80 text-xs uppercase text-slate-400">
                    <tr><th className="text-left px-4 py-3">{dict.operators.cols.name}</th><th className="text-left px-4 py-3">{dict.operators.cols.phone}</th><th className="text-left px-4 py-3">{dict.operators.cols.license}</th><th className="text-left px-4 py-3">{dict.operators.cols.licenseExpiry}</th><th className="text-left px-4 py-3">{dict.operators.cols.salary}</th><th className="text-left px-4 py-3">{dict.operators.cols.user}</th><th className="text-right px-4 py-3">{dict.common.actions}</th></tr>
                </thead>
                <tbody>
                    {loading && <tr><td colSpan={7} className="px-4 py-10 text-center"><Loader2 className="animate-spin inline"/></td></tr>}
                    {!loading && items.length===0 && <tr><td colSpan={7} className="px-0 py-0"><EmptyState icon={HardHat} title={dict.operators.empty} ctaLabel={dict.operators.newOperator} ctaHref="/operadores/nuevo" testid="operators-empty"/></td></tr>}
                    {!loading && items.map(o => <tr key={o.id} data-testid={`operator-row-${o.id}`} className="border-t border-slate-800 hover:bg-slate-900/60">
                        <td className="px-4 py-3"><Link to={`/operadores/${o.id}`} className="text-slate-100 hover:text-brand-orange">{o.full_name}</Link></td>
                        <td className="px-4 py-3 text-slate-300">{o.phone||"—"}</td>
                        <td className="px-4 py-3 text-slate-300">{o.license_number||"—"}</td>
                        <td className="px-4 py-3"><LicenseBadge expiry={o.license_expiry}/></td>
                        <td className="px-4 py-3 text-slate-300">{fmtMXN(o.base_monthly_salary_mxn)}</td>
                        <td className="px-4 py-3 text-slate-400">{o.user_email||"—"}</td>
                        <td className="px-4 py-3"><div className="flex justify-end gap-1">
                            <Link to={`/operadores/${o.id}/editar`} data-testid={`edit-operator-${o.id}`} className="p-2 rounded hover:bg-slate-800"><Pencil size={16}/></Link>
                            <button onClick={()=>setConfirm({id:o.id,archived:o.archived,title:o.archived?dict.common.confirmRestore:dict.common.confirmArchive})} data-testid={`archive-operator-${o.id}`} className={`p-2 rounded hover:bg-slate-800 ${o.archived?"text-emerald-400":"text-red-400"}`}>{o.archived?<ArchiveRestore size={16}/>:<Archive size={16}/>}</button>
                        </div></td>
                    </tr>)}
                </tbody>
            </table>
        </div>
        <ConfirmDialog open={!!confirm} title={confirm?.title} onCancel={()=>setConfirm(null)} onConfirm={()=>doArchive(confirm.id,confirm.archived)}/>
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

export function OperatorForm() {
    const { id } = useParams(); const isEdit = !!id; const navigate = useNavigate();
    const [availableUsers, setAvailableUsers] = useState([]);
    const [userMode, setUserMode] = useState("none"); // none / existing / create
    const [toast, setToast] = useState(null);
    const form = useForm({
        initial: { full_name:"", phone:"", license_number:"", license_expiry:"",
            base_monthly_salary_mxn:"", base_per_day_mxn:"", commission_pct:"", bonus_fixed_mxn:"",
            existing_user_id:"", email:"", password:"" },
        validators: {
            full_name: required,
            email: (v, vs) => userMode==="create" ? compose(required, emailV)(v) : null,
            password: (v) => userMode==="create" ? compose(required, passwordMin)(v) : null,
            existing_user_id: (v) => userMode==="existing" ? required(v) : null,
            phone: phoneV,
        },
    });

    useEffect(() => {
        if (!isEdit) api.get("/users",{params:{role:"operator",is_active:true}}).then(r => setAvailableUsers(r.data.filter(u=>!u.linked_operator_id)));
        if (isEdit) api.get(`/operators/${id}`).then(r => {
            const o = r.data;
            form.setValues({ full_name:o.full_name, phone:o.phone||"", license_number:o.license_number||"",
                license_expiry: (o.license_expiry||"").slice(0,10),
                base_monthly_salary_mxn: o.base_monthly_salary_mxn??"",
                base_per_day_mxn: o.default_external_rates?.base_per_day_mxn??"",
                commission_pct: o.default_external_rates?.commission_pct??"",
                bonus_fixed_mxn: o.default_external_rates?.bonus_fixed_mxn??"",
            });
        });
    }, [id]); // eslint-disable-line

    const onSubmit = (e) => {
        e.preventDefault();
        form.handleSubmit(async (v) => {
            const body = {
                full_name: v.full_name.trim(), phone: v.phone||null,
                license_number: v.license_number||null,
                license_expiry: v.license_expiry? new Date(v.license_expiry).toISOString():null,
                base_monthly_salary_mxn: v.base_monthly_salary_mxn===""?null:Number(v.base_monthly_salary_mxn),
                default_external_rates: {
                    base_per_day_mxn: v.base_per_day_mxn===""?null:Number(v.base_per_day_mxn),
                    commission_pct: v.commission_pct===""?null:Number(v.commission_pct),
                    bonus_fixed_mxn: v.bonus_fixed_mxn===""?null:Number(v.bonus_fixed_mxn),
                },
            };
            if (!isEdit) {
                if (userMode==="existing") body.user = { existing_user_id: v.existing_user_id };
                else if (userMode==="create") body.user = { email: v.email, password: v.password };
            }
            try {
                if (isEdit) await api.patch(`/operators/${id}`, body);
                else await api.post("/operators", body);
                setToast({msg:dict.common.saved,type:"success"});
                setTimeout(()=>navigate("/operadores"), 600);
            } catch (err) { setToast({msg:formatApiError(err),type:"error"}); }
        });
    };

    return <div data-testid="operator-form" className="space-y-5 max-w-3xl">
        <Link to="/operadores" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft size={14}/>{dict.operators.title}</Link>
        <h1 className="font-display text-4xl font-extrabold">{isEdit?dict.operators.editOperator:dict.operators.newOperator}</h1>
        <form onSubmit={onSubmit} noValidate className="space-y-6">
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.operators.sections.personal}</h2>
                <Field label={dict.operators.fields.fullName} required error={form.errors.full_name}><TextInput {...form.field("full_name")} error={form.errors.full_name} data-testid="op-fullname"/></Field>
                <Field label={dict.operators.fields.phone} error={form.errors.phone}><TextInput {...form.field("phone")} error={form.errors.phone} data-testid="op-phone"/></Field>
            </section>
            {!isEdit && <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.operators.sections.account}</h2>
                <Field label={dict.operators.fields.userMode}>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={userMode==="none"} onChange={()=>setUserMode("none")} data-testid="op-mode-none"/>{dict.operators.fields.modeNone}</label>
                        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={userMode==="existing"} onChange={()=>setUserMode("existing")} data-testid="op-mode-existing"/>{dict.operators.fields.modeExisting}</label>
                        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={userMode==="create"} onChange={()=>setUserMode("create")} data-testid="op-mode-create"/>{dict.operators.fields.modeCreate}</label>
                    </div>
                </Field>
                {userMode==="existing" && (availableUsers.length===0 ? <p className="text-xs text-slate-500">{dict.operators.noAvailableUsers}</p> :
                    <Field label={dict.operators.fields.existingUser} error={form.errors.existing_user_id}>
                        <SelectInput {...form.field("existing_user_id")} error={form.errors.existing_user_id} data-testid="op-existing-user">
                            <option value="">—</option>
                            {availableUsers.map(u=><option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>)}
                        </SelectInput>
                    </Field>)}
                {userMode==="create" && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label={dict.operators.fields.email} required error={form.errors.email}><TextInput type="email" {...form.field("email")} error={form.errors.email} data-testid="op-email"/></Field>
                    <Field label={dict.operators.fields.password} required error={form.errors.password} hint={dict.users.fields.passwordHint}><TextInput type="password" {...form.field("password")} error={form.errors.password} data-testid="op-password"/></Field>
                </div>}
            </section>}
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.operators.sections.license}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label={dict.operators.fields.licenseNumber}><TextInput {...form.field("license_number")}/></Field>
                    <Field label={dict.operators.fields.licenseExpiry}><DateInput {...form.field("license_expiry")}/></Field>
                </div>
            </section>
            <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
                <h2 className="font-display text-lg font-bold">{dict.operators.sections.compensation}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label={dict.operators.fields.baseSalary}><NumberInput {...form.field("base_monthly_salary_mxn")} data-testid="op-salary"/></Field>
                    <Field label={dict.operators.fields.extBase}><NumberInput {...form.field("base_per_day_mxn")}/></Field>
                    <Field label={dict.operators.fields.extCommission}><NumberInput step="0.1" {...form.field("commission_pct")}/></Field>
                    <Field label={dict.operators.fields.extBonus}><NumberInput {...form.field("bonus_fixed_mxn")}/></Field>
                </div>
            </section>
            <div className="flex justify-end gap-2">
                <Link to="/operadores" className="h-11 px-5 rounded-lg border border-slate-700 inline-flex items-center text-sm">{dict.common.cancel}</Link>
                <button type="submit" disabled={form.submitting} data-testid="op-submit" className="h-11 px-5 rounded-lg bg-brand-orange text-slate-950 font-semibold uppercase text-sm inline-flex items-center gap-2 disabled:opacity-60">{form.submitting && <Loader2 className="animate-spin" size={16}/>}{form.submitting?dict.common.saving:dict.common.save}</button>
            </div>
        </form>
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

export function OperatorDetail() {
    const { id } = useParams();
    const [o, setO] = useState(null);
    const [toast, setToast] = useState(null);
    const [uploading, setUploading] = useState(false);

    const load = useCallback(() => api.get(`/operators/${id}`).then(r=>setO(r.data)), [id]);
    useEffect(() => { load(); }, [load]);

    const onUpload = async (e) => {
        const file = e.target.files?.[0]; e.target.value=""; if (!file) return;
        setUploading(true);
        const fd = new FormData(); fd.append("file", file);
        try { await api.post(`/operators/${id}/documents`, fd); await load(); setToast({msg:dict.common.saved,type:"success"}); }
        catch (err) { setToast({msg:formatApiError(err),type:"error"}); }
        finally { setUploading(false); }
    };
    const onDelete = async (docId) => {
        try { await api.delete(`/operators/${id}/documents/${docId}`); await load(); }
        catch (err) { setToast({msg:formatApiError(err),type:"error"}); }
    };

    if (!o) return <Loader2 className="animate-spin"/>;
    const Row = ({k,v}) => <div className="flex justify-between py-2 border-b border-slate-800 last:border-0"><span className="text-xs uppercase text-slate-500">{k}</span><span className="text-sm">{v||"—"}</span></div>;
    return <div data-testid="operator-detail" className="space-y-5 max-w-3xl">
        <Link to="/operadores" className="inline-flex items-center gap-1 text-sm text-slate-400"><ArrowLeft size={14}/>{dict.operators.title}</Link>
        <div className="flex items-start justify-between flex-wrap gap-3">
            <h1 className="font-display text-4xl font-extrabold">{o.full_name}</h1>
            <Link to={`/operadores/${o.id}/editar`} className="h-10 px-4 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm inline-flex items-center gap-1"><Pencil size={14}/>{dict.common.edit}</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="font-display text-lg font-bold mb-3">{dict.operators.sections.personal}</h2>
                <Row k="Teléfono" v={o.phone}/><Row k="Usuario" v={o.user_email}/>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="font-display text-lg font-bold mb-3">{dict.operators.sections.license}</h2>
                <Row k="Número" v={o.license_number}/><Row k="Vence" v={o.license_expiry?<LicenseBadge expiry={o.license_expiry}/>:null}/>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 md:col-span-2">
                <h2 className="font-display text-lg font-bold mb-3">{dict.operators.sections.compensation}</h2>
                <Row k="Sueldo base" v={fmtMXN(o.base_monthly_salary_mxn)}/>
                <Row k="Base/día ext" v={fmtMXN(o.default_external_rates?.base_per_day_mxn)}/>
                <Row k="Comisión" v={o.default_external_rates?.commission_pct!=null?`${o.default_external_rates.commission_pct}%`:null}/>
                <Row k="Bono" v={fmtMXN(o.default_external_rates?.bonus_fixed_mxn)}/>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 md:col-span-2">
                <h2 className="font-display text-lg font-bold mb-3">{dict.operators.sections.documents}</h2>
                <p className="text-xs text-slate-500 mb-3">{dict.operators.documentsHint}</p>
                <div className="flex flex-wrap gap-3 mb-3" data-testid="op-documents-list">
                    {o.document_photo_ids?.length===0 && <p className="text-xs text-slate-500">Sin documentos.</p>}
                    {o.document_photo_ids?.map(pid => <div key={pid} className="relative group">
                        <PhotoThumb photoId={pid} size={96}/>
                        <button onClick={()=>onDelete(pid)} data-testid={`del-doc-${pid}`} className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100"><Trash2 size={12}/></button>
                    </div>)}
                </div>
                <label className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-700 hover:border-brand-orange cursor-pointer text-sm">
                    {uploading? <Loader2 className="animate-spin" size={14}/> : <Upload size={14}/>}{dict.operators.addDocument}
                    <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="op-upload-doc"/>
                </label>
            </div>
        </div>
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

// ============================================================
// CLIENTS
// ============================================================

export function ClientsList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]); const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [archived, setArchived] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [toast, setToast] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (archived) params.archived = true;
            const r = await api.get("/clients",{params});
            setItems(r.data);
        }
        catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
        finally { setLoading(false); }
    }, [q, archived]);
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

    const doArchive = async (id, isArchived) => {
        setConfirm(null);
        try { await api.post(`/clients/${id}/${isArchived?"restore":"archive"}`); load(); setToast({msg:dict.common.saved,type:"success"}); }
        catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
    };

    return <div data-testid="clients-page" className="space-y-5">
        <header className="flex items-start justify-between flex-wrap gap-3">
            <div><h1 className="font-display text-4xl sm:text-5xl font-extrabold">{dict.clients.title}</h1><p className="mt-2 text-slate-400 text-sm">{dict.clients.subtitle}</p></div>
            <button onClick={()=>navigate("/clientes/nuevo")} data-testid="new-client-button" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-brand-orange text-slate-950 font-semibold uppercase tracking-wider text-sm"><Plus size={18}/>{dict.clients.newClient}</button>
        </header>
        <div className="flex gap-3 flex-wrap items-end">
            <div className="relative max-w-md flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
                <input value={q} onChange={e=>setQ(e.target.value)} placeholder={dict.clients.searchPlaceholder} data-testid="client-search" className="w-full h-10 pl-9 pr-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 h-10"><input type="checkbox" checked={archived} onChange={e=>setArchived(e.target.checked)} data-testid="filter-archived"/>{dict.common.showArchived}</label>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="clients-table">
                <thead className="bg-slate-900/80 text-xs uppercase text-slate-400">
                    <tr><th className="text-left px-4 py-3">{dict.clients.cols.name}</th><th className="text-left px-4 py-3">{dict.clients.cols.contact}</th><th className="text-left px-4 py-3">{dict.clients.cols.phone}</th><th className="text-left px-4 py-3">{dict.clients.cols.email}</th><th className="text-right px-4 py-3">{dict.common.actions}</th></tr>
                </thead>
                <tbody>
                    {loading && <tr><td colSpan={5} className="px-4 py-10 text-center"><Loader2 className="animate-spin inline"/></td></tr>}
                    {!loading && items.length===0 && <tr><td colSpan={5} className="px-0 py-0"><EmptyState icon={Users2} title={dict.clients.empty} ctaLabel={dict.clients.newClient} ctaHref="/clientes/nuevo" testid="clients-empty"/></td></tr>}
                    {!loading && items.map(c => <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-t border-slate-800 hover:bg-slate-900/60">
                        <td className="px-4 py-3">
                            <Link to={`/clientes/${c.id}`} className="text-slate-100 hover:text-brand-orange">{c.name}</Link>
                            {c.archived && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500 border border-slate-700 rounded px-1.5 py-0.5">{dict.common.archived}</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-300">{c.contact_person||"—"}</td>
                        <td className="px-4 py-3 text-slate-300">{c.phone||"—"}</td>
                        <td className="px-4 py-3 text-slate-300">{c.email||"—"}</td>
                        <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                                <Link to={`/clientes/${c.id}/editar`} data-testid={`edit-client-${c.id}`} title={dict.common.edit} className="p-2 rounded hover:bg-slate-800"><Pencil size={16}/></Link>
                                <button onClick={()=>setConfirm({id:c.id,archived:c.archived,title:c.archived?dict.common.confirmRestore:dict.common.confirmArchive})} data-testid={`archive-client-${c.id}`} title={c.archived?dict.common.restore:dict.common.archive} className={`p-2 rounded hover:bg-slate-800 ${c.archived?"text-emerald-400":"text-red-400"}`}>{c.archived?<ArchiveRestore size={16}/>:<Archive size={16}/>}</button>
                            </div>
                        </td>
                    </tr>)}
                </tbody>
            </table>
        </div>
        <ConfirmDialog open={!!confirm} title={confirm?.title} onCancel={()=>setConfirm(null)} onConfirm={()=>doArchive(confirm.id,confirm.archived)}/>
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

export function ClientForm() {
    const { id } = useParams(); const isEdit = !!id; const navigate = useNavigate();
    const [toast, setToast] = useState(null);
    const form = useForm({
        initial:{ name:"", contact_person:"", phone:"", email:"", address:"", notes:"" },
        validators:{ name: required, email: emailV, phone: phoneV }
    });
    useEffect(() => { if (isEdit) api.get(`/clients/${id}`).then(r => form.setValues(r.data)); }, [id]); // eslint-disable-line
    const onSubmit = (e) => { e.preventDefault();
        form.handleSubmit(async (v) => {
            const body = { ...v, email: v.email||null, phone: v.phone||null };
            try { if (isEdit) await api.patch(`/clients/${id}`, body); else await api.post("/clients", body);
                setToast({msg:dict.common.saved,type:"success"}); setTimeout(()=>navigate("/clientes"), 600);
            } catch (err) { setToast({msg:formatApiError(err),type:"error"}); }
        });
    };
    return <div data-testid="client-form" className="space-y-5 max-w-2xl">
        <Link to="/clientes" className="inline-flex items-center gap-1 text-sm text-slate-400"><ArrowLeft size={14}/>{dict.clients.title}</Link>
        <h1 className="font-display text-4xl font-extrabold">{isEdit?dict.clients.editClient:dict.clients.newClient}</h1>
        <form onSubmit={onSubmit} noValidate className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 space-y-4">
            <Field label={dict.clients.fields.name} required error={form.errors.name}><TextInput {...form.field("name")} error={form.errors.name} data-testid="cl-name"/></Field>
            <Field label={dict.clients.fields.contact}><TextInput {...form.field("contact_person")}/></Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={dict.clients.fields.phone} error={form.errors.phone}><TextInput {...form.field("phone")} error={form.errors.phone}/></Field>
                <Field label={dict.clients.fields.email} error={form.errors.email}><TextInput type="email" {...form.field("email")} error={form.errors.email}/></Field>
            </div>
            <Field label={dict.clients.fields.address}><Textarea rows={2} {...form.field("address")}/></Field>
            <Field label={dict.clients.fields.notes}><Textarea rows={3} {...form.field("notes")}/></Field>
            <div className="flex justify-end gap-2">
                <Link to="/clientes" className="h-11 px-5 rounded-lg border border-slate-700 inline-flex items-center text-sm">{dict.common.cancel}</Link>
                <button type="submit" disabled={form.submitting} data-testid="cl-submit" className="h-11 px-5 rounded-lg bg-brand-orange text-slate-950 font-semibold uppercase text-sm inline-flex items-center gap-2 disabled:opacity-60">{form.submitting && <Loader2 className="animate-spin" size={16}/>}{form.submitting?dict.common.saving:dict.common.save}</button>
            </div>
        </form>
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

export function ClientDetail() {
    const { id } = useParams();
    const [c, setC] = useState(null);
    useEffect(() => { api.get(`/clients/${id}`).then(r=>setC(r.data)); }, [id]);
    if (!c) return <Loader2 className="animate-spin"/>;
    const Row = ({k,v}) => <div className="flex justify-between py-2 border-b border-slate-800 last:border-0"><span className="text-xs uppercase text-slate-500">{k}</span><span className="text-sm">{v||"—"}</span></div>;
    return <div data-testid="client-detail" className="space-y-5 max-w-2xl">
        <Link to="/clientes" className="inline-flex items-center gap-1 text-sm text-slate-400"><ArrowLeft size={14}/>{dict.clients.title}</Link>
        <div className="flex items-start justify-between flex-wrap gap-3">
            <h1 className="font-display text-4xl font-extrabold">{c.name}</h1>
            <Link to={`/clientes/${c.id}/editar`} className="h-10 px-4 rounded-lg border border-slate-700 text-sm inline-flex items-center gap-1"><Pencil size={14}/>{dict.common.edit}</Link>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
            <Row k="Contacto" v={c.contact_person}/><Row k="Teléfono" v={c.phone}/><Row k="Correo" v={c.email}/><Row k="Dirección" v={c.address}/><Row k="Notas" v={c.notes}/>
        </div>
        <div className="rounded-xl border border-dashed border-slate-700 p-5 text-center text-sm text-slate-500">{dict.clients.detail.placeholder}</div>
    </div>;
}
